<template>
  <div class="inventory-view">
    <div class="page-header">
      <div>
        <h1>Equipment Inventory</h1>
        <p>Manage and track equipment inventory</p>
      </div>
      <div class="header-actions">
        <button @click="openAddModal" class="btn-add">+ Add Equipment</button>
      </div>
    </div>

    <InventoryFilters
      :search="searchQuery"
      :type="filterType"
      :status="filterStatus"
      @update:search="searchQuery = $event"
      @update:type="filterType = $event"
      @update:status="filterStatus = $event"
    />


    <InventoryTable
      :equipment="filteredEquipment"
      :loading="loading"
      :is-admin="isAdmin"
      :selected-items="selectedItems"
      @update:selectedItems="selectedItems = $event"
      @edit="openEditModal"
      @delete="deleteEquipment"
      @claim="openClaimByNameModal"
      @bulkEdit="openBulkEditModal"
      @printPackingSlips="printPackingSlips"
      @printDonationReceipts="printDonationReceipts"
      @addMoreOfModel="openAddMoreModal"
    />

    <EquipmentModal
      :show="showModal"
      :is-editing="isEditing"
      :form-data="formData"
      :submitting="submitting"
      :error="error"
      @close="closeModal"
      @submit="submitEquipment"
    />

    <!-- Bulk Edit Modal -->
    <BulkEditModal
      :show="showBulkEditModal"
      :item-count="bulkEditEquipmentIds.length"
      :submitting="bulkEditSubmitting"
      :error="bulkEditError"
      @close="closeBulkEditModal"
      @submit="confirmBulkEdit"
    />

    <!-- Claim by Name Modal -->
    <v-dialog v-model="showClaimByNameModal" max-width="500px">
      <v-card>
        <v-card-title>
          <h2>Claim Equipment</h2>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            Claim <strong>{{ equipmentToClaim?.manufacturer }} {{ equipmentToClaim?.model }}</strong>
          </p>

          <v-text-field
            v-model="claimedForName"
            label="Recipient Name"
            placeholder="Enter student or teacher name"
            variant="outlined"
            autofocus
            :error-messages="claimError"
          ></v-text-field>

          <v-alert type="info" variant="tonal">
            This will mark the equipment as claimed for the specified person.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeClaimByNameModal">Cancel</v-btn>
          <v-btn
            color="success"
            @click="confirmClaimByName"
            :disabled="!claimedForName || claimedForName.trim() === ''"
          >
            Claim
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print Format Selection Modal -->
    <v-dialog v-model="showPrintFormatModal" max-width="500px">
      <v-card>
        <v-card-title>
          <h2>Select Print Format</h2>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            Choose a print format for {{ printEquipmentIds.length }} item(s):
          </p>

          <v-radio-group v-model="selectedPrintFormat" class="mt-2">
            <v-radio value="slips">
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Packing Slips</div>
                  <div class="text-caption text-grey">Full-page slips (3 per 8.5" x 11" page) - detailed info cards</div>
                </div>
              </template>
            </v-radio>
            <v-radio value="labels" class="mt-3">
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Adhesive Labels</div>
                  <div class="text-caption text-grey">4" x 3.33" labels (6 per 8.5" x 11" sheet) - for label paper</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closePrintFormatModal">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="confirmPrint"
            :loading="printLoading"
          >
            <v-icon start>mdi-printer</v-icon>
            Print
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Donation Receipt Modal -->
    <v-dialog v-model="showReceiptModal" max-width="560px">
      <v-card>
        <v-card-title>
          <h2>Print Donation Receipt</h2>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            Generate a receipt for {{ receiptEquipmentIds.length }} item(s).
          </p>

          <v-radio-group v-model="receiptGroupByDonor" class="mt-2">
            <v-radio :value="true">
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">One receipt per donor</div>
                  <div class="text-caption text-grey">
                    Items are grouped by donor, each listed with its own donation date and value
                  </div>
                </div>
              </template>
            </v-radio>
            <v-radio :value="false" class="mt-3">
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">One receipt per item</div>
                  <div class="text-caption text-grey">A separate receipt page for every selected item</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-alert v-if="receiptItemsMissingDonor.length" type="warning" variant="tonal" class="mt-2">
            {{ receiptItemsMissingDonor.length }} of the selected item(s) have no donor recorded.
            They will print under "Donor not recorded".
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeReceiptModal">Cancel</v-btn>
          <v-btn color="primary" @click="confirmPrintReceipts" :loading="receiptLoading">
            <v-icon start>mdi-receipt-text-outline</v-icon>
            Print
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add More of Model Modal -->
    <v-dialog v-model="showAddMoreModal" max-width="600px">
      <v-card>
        <v-card-title>
          <h2>Add More of This Model</h2>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            Add more <strong>{{ equipmentToClone?.manufacturer }} {{ equipmentToClone?.model }}</strong>
          </p>

          <v-text-field
            v-model.number="addMoreQuantity"
            label="Quantity"
            type="number"
            min="1"
            max="50"
            variant="outlined"
            class="mb-3"
          ></v-text-field>

          <v-select
            v-model="addMoreStatus"
            label="Status"
            :items="[
              { title: 'Available', value: 'available' },
              { title: 'Processing', value: 'processing' },
              { title: 'Troubleshooting', value: 'troubleshooting' },
              { title: 'Parts', value: 'parts' },
              { title: 'Claimed', value: 'claimed' },
              { title: 'Distributed', value: 'distributed' }
            ]"
            variant="outlined"
            class="mb-3"
          ></v-select>

          <v-text-field
            v-model="addMoreLocation"
            label="Location"
            placeholder="e.g., Warehouse A, Shelf 3"
            variant="outlined"
            class="mb-3"
          ></v-text-field>

          <v-text-field
            v-model.number="addMoreValue"
            label="Value ($)"
            type="number"
            min="0"
            step="0.01"
            prefix="$"
            variant="outlined"
            class="mb-3"
            hint="Copied from the original item - change it here if these are worth something different"
            persistent-hint
          ></v-text-field>

          <v-checkbox
            v-if="equipmentToClone?.donor"
            v-model="addMoreCopyDonor"
            :label="`Same donor as original (${equipmentToClone?.donor})`"
            density="compact"
            hide-details
            class="mb-2"
          ></v-checkbox>

          <v-text-field
            v-model="addMoreDonor"
            label="Donor"
            placeholder="Leave blank if these were not donated by anyone recorded"
            variant="outlined"
            class="mb-3"
            hint="The donor is not copied unless you specify it here"
            persistent-hint
          ></v-text-field>

          <v-text-field
            v-model="addMoreDonatedOn"
            label="Donation Date"
            type="date"
            variant="outlined"
            class="mb-3"
          ></v-text-field>

          <v-alert type="info" variant="tonal" class="mb-3">
            This will create {{ addMoreQuantity || 0 }} new equipment item(s) with the following specifications:
          </v-alert>

          <v-card variant="outlined" class="pa-3">
            <v-row dense>
              <v-col cols="6">
                <div class="text-caption text-grey">Type</div>
                <div>{{ equipmentToClone?.equipment_type }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-grey">Rating</div>
                <div>{{ equipmentToClone?.rating || 'N/A' }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-grey">CPU</div>
                <div>{{ equipmentToClone?.cpu || 'N/A' }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-grey">RAM</div>
                <div>{{ equipmentToClone?.ram || 'N/A' }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-grey">Storage</div>
                <div>{{ equipmentToClone?.storage || 'N/A' }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-grey">GPU</div>
                <div>{{ equipmentToClone?.gpu || 'N/A' }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-grey">OS</div>
                <div>{{ equipmentToClone?.os || 'N/A' }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-grey">Screen</div>
                <div>{{ equipmentToClone?.screen || 'N/A' }}</div>
              </v-col>
            </v-row>
          </v-card>

          <v-alert v-if="addMoreError" type="error" variant="tonal" class="mt-3">
            {{ addMoreError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeAddMoreModal">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="confirmAddMore"
            :disabled="!addMoreQuantity || addMoreQuantity < 1 || addMoreSubmitting"
            :loading="addMoreSubmitting"
          >
            Add {{ addMoreQuantity || 0 }} Item(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { api } from '@/composables/api'
import { useAuthStore } from '@/store/auth'
import InventoryFilters from '@/components/inventory/InventoryFilters.vue'
import BulkActionsBar from '@/components/inventory/BulkActionsBar.vue'
import InventoryTable from '@/components/inventory/InventoryTable.vue'
import EquipmentModal from '@/components/inventory/EquipmentModal.vue'
import BulkEditModal from '@/components/inventory/BulkEditModal.vue'

const authStore = useAuthStore()
const loading = ref(true)
const equipment = ref<any[]>([])
const searchQuery = ref('')
const filterType = ref('')
const filterStatus = ref<string[]>(['processing', 'available'])

// Selection state
const selectedItems = ref<string[]>([])

// Modal state
const showModal = ref(false)
const isEditing = ref(false)
const editingId = ref<string | null>(null)
const submitting = ref(false)
const error = ref('')

// Claim by name modal state
const showClaimByNameModal = ref(false)
const equipmentToClaim = ref<any>(null)
const claimedForName = ref('')
const claimError = ref('')

// Bulk edit modal state
const showBulkEditModal = ref(false)
const bulkEditEquipmentIds = ref<string[]>([])
const bulkEditSubmitting = ref(false)
const bulkEditError = ref('')

// Add more of model modal state
const showAddMoreModal = ref(false)
const equipmentToClone = ref<any>(null)
const addMoreQuantity = ref(1)
const addMoreStatus = ref('processing')
const addMoreLocation = ref('')
const addMoreValue = ref<number | null>(null)
const addMoreDonor = ref('')
const addMoreDonatedOn = ref('')
const addMoreCopyDonor = ref(false)
const addMoreSubmitting = ref(false)
const addMoreError = ref('')

// Donation receipt modal state
const showReceiptModal = ref(false)
const receiptEquipmentIds = ref<string[]>([])
const receiptGroupByDonor = ref(true)
const receiptLoading = ref(false)

// Print format modal state
const showPrintFormatModal = ref(false)
const printEquipmentIds = ref<string[]>([])
const selectedPrintFormat = ref('slips')
const printLoading = ref(false)

// Check if user is admin
const isAdmin = computed(() => {
  return authStore.authUser && authStore.authUser.isAdmin()
})

// Form data
const formData = ref({
  equipment_type: 'laptop',
  quantity: 1,
  manufacturer: '',
  model: '',
  rating: null as number | null,
  screen: '',
  cpu: '',
  ram: '',
  storage: '',
  gpu: '',
  os: '',
  charger: '',
  status: 'available',
  location: '',
  asset_tag: '',
  comments: '',
  value: null as number | null,
  donor: '',
  donated_on: ''
})

const filteredEquipment = computed(() => {
  let filtered = equipment.value

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(item =>
      (item.equipment_type?.toLowerCase() || '').includes(query) ||
      (item.manufacturer?.toLowerCase() || '').includes(query) ||
      (item.model?.toLowerCase() || '').includes(query) ||
      (item.location?.toLowerCase() || '').includes(query) ||
      (item.asset_tag?.toLowerCase() || '').includes(query) ||
      (item.donor?.toLowerCase() || '').includes(query)
    )
  }

  // Apply type filter
  if (filterType.value) {
    filtered = filtered.filter(item => item.equipment_type === filterType.value)
  }

  // Apply status filter
  if (filterStatus.value && filterStatus.value.length > 0) {
    filtered = filtered.filter(item => filterStatus.value.includes(item.status))
  }

  return filtered
})


const printSlips = () => {
  alert(`Printing slips for ${selectedItems.value.length} items`)
  // TODO: Implement print slip functionality
}

const markAsDelivered = () => {
  const location = prompt('Enter delivery location:')
  if (location) {
    alert(`Marking ${selectedItems.value.length} items as delivered to ${location}`)
    // TODO: Implement mark as delivered functionality
    selectedItems.value = []
  }
}

// Convert an API datetime (ISO string) into a value the <input type="date"> accepts
const toDateInput = (value?: string | null) => {
  if (!value) return ''
  return String(value).slice(0, 10)
}

// An emptied number input yields '' rather than null, which the API cannot parse as a float
const normalizeValue = (value: any): number | null => {
  if (value === '' || value === null || value === undefined || Number.isNaN(value)) {
    return null
  }
  return Number(value)
}

// Strip empty optional fields so the API does not try to parse '' as a number/date
const buildEquipmentPayload = (data: any) => {
  const payload = { ...data }
  delete payload.quantity

  payload.value = normalizeValue(payload.value)
  if (!payload.donated_on) {
    delete payload.donated_on
  }
  payload.donor = (payload.donor || '').trim()
  payload.charger = (payload.charger || '').trim()

  return payload
}

const loadEquipment = async () => {
  loading.value = true
  try {
    const response = await api.get('/equipment')
    if (response.data) {
      equipment.value = response.data
    }
  } catch (error) {
    console.error('Error loading equipment:', error)
  } finally {
    loading.value = false
  }
}

const openAddModal = () => {
  isEditing.value = false
  editingId.value = null
  formData.value = {
    equipment_type: 'laptop',
    quantity: 1,
    manufacturer: '',
    model: '',
    rating: null,
    screen: '',
    cpu: '',
    ram: '',
    storage: '',
    gpu: '',
    os: '',
    charger: '',
    status: 'available',
    location: '',
    asset_tag: '',
    comments: '',
    value: null,
    donor: '',
    donated_on: ''
  }
  showModal.value = true
  error.value = ''
}

const openEditModal = (item: any) => {
  isEditing.value = true
  editingId.value = item.id
  formData.value = {
    equipment_type: item.equipment_type || '',
    quantity: 1,
    manufacturer: item.manufacturer || '',
    model: item.model || '',
    rating: item.rating || null,
    screen: item.screen || '',
    cpu: item.cpu || '',
    ram: item.ram || '',
    storage: item.storage || '',
    gpu: item.gpu || '',
    os: item.os || '',
    charger: item.charger || '',
    status: item.status || 'available',
    location: item.location || '',
    asset_tag: item.asset_tag || '',
    comments: item.comments || '',
    value: item.value ?? null,
    donor: item.donor || '',
    donated_on: toDateInput(item.donated_on)
  }
  showModal.value = true
  error.value = ''
}

const closeModal = () => {
  showModal.value = false
  formData.value = {
    equipment_type: 'laptop',
    quantity: 1,
    manufacturer: '',
    model: '',
    rating: null,
    screen: '',
    cpu: '',
    ram: '',
    storage: '',
    gpu: '',
    os: '',
    charger: '',
    status: 'available',
    location: '',
    asset_tag: '',
    comments: '',
    value: null,
    donor: '',
    donated_on: ''
  }
  error.value = ''
}

const submitEquipment = async (data: any) => {
  submitting.value = true
  error.value = ''

  try {
    if (isEditing.value && editingId.value) {
      // Update existing equipment
      const equipmentData = buildEquipmentPayload(data)
      await api.put(`/equipment/${editingId.value}`, equipmentData)
    } else {
      // Add new equipment
      const quantity = data.quantity
      const equipmentData = buildEquipmentPayload(data)

      // Add multiple items if quantity > 1
      for (let i = 0; i < quantity; i++) {
        await api.post('/equipment', equipmentData)
      }
    }

    await loadEquipment()
    closeModal()
  } catch (error: any) {
    console.error('Error saving equipment:', error)
    if (error.response?.status === 403) {
      error.value = 'You do not have permission to perform this action.'
    } else {
      error.value = 'Failed to save equipment. Please try again.'
    }
  } finally {
    submitting.value = false
  }
}

const deleteEquipment = async (item: any) => {
  if (confirm(`Are you sure you want to delete this ${item.equipment_type}?`)) {
    try {
      await api.delete(`/equipment/${item.id}`)
      await loadEquipment()
    } catch (error: any) {
      console.error('Error deleting equipment:', error)
      if (error.response?.status === 403) {
        alert('You do not have permission to delete equipment.')
      } else {
        alert('Failed to delete equipment')
      }
    }
  }
}

const openClaimByNameModal = (item: any) => {
  equipmentToClaim.value = item
  claimedForName.value = ''
  claimError.value = ''
  showClaimByNameModal.value = true
}

const closeClaimByNameModal = () => {
  showClaimByNameModal.value = false
  equipmentToClaim.value = null
  claimedForName.value = ''
  claimError.value = ''
}

const confirmClaimByName = async () => {
  if (!equipmentToClaim.value || !claimedForName.value.trim()) {
    claimError.value = 'Please enter a recipient name'
    return
  }

  try {
    await api.patch(`/equipment/${equipmentToClaim.value.id}/claim?claimed_for=${encodeURIComponent(claimedForName.value.trim())}`)
    await loadEquipment()
    closeClaimByNameModal()
  } catch (error: any) {
    console.error('Error claiming equipment:', error)
    claimError.value = 'Failed to claim equipment. Please try again.'
  }
}

// Print packing slips function - opens format selection modal
const printPackingSlips = (equipmentIds: string[]) => {
  printEquipmentIds.value = equipmentIds
  selectedPrintFormat.value = 'slips'
  showPrintFormatModal.value = true
}

const closePrintFormatModal = () => {
  showPrintFormatModal.value = false
  printEquipmentIds.value = []
  selectedPrintFormat.value = 'slips'
}

const confirmPrint = async () => {
  if (!printEquipmentIds.value.length) {
    return
  }

  printLoading.value = true

  try {
    // Call the packing slip API endpoint with format selection
    const response = await api.post('/equipment/packing-slips', {
      equipment_ids: printEquipmentIds.value,
      template_type: selectedPrintFormat.value
    })

    // Open the HTML in a new window for printing
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(response.data)
      printWindow.document.close()
      // Optionally trigger print dialog
      setTimeout(() => {
        printWindow.focus()
        // printWindow.print() // Uncomment to auto-open print dialog
      }, 250)
    } else {
      alert('Please allow popups for this site to print packing slips.')
    }

    closePrintFormatModal()
  } catch (error: any) {
    console.error('Error generating packing slips:', error)
    alert('Failed to generate packing slips. Please try again.')
  } finally {
    printLoading.value = false
  }
}

// Bulk edit functions
const openBulkEditModal = (equipmentIds: string[]) => {
  bulkEditEquipmentIds.value = equipmentIds
  bulkEditError.value = ''
  showBulkEditModal.value = true
}

const closeBulkEditModal = () => {
  showBulkEditModal.value = false
  bulkEditEquipmentIds.value = []
  bulkEditError.value = ''
}

const confirmBulkEdit = async (updateData: any) => {
  if (!bulkEditEquipmentIds.value.length) {
    bulkEditError.value = 'No equipment selected'
    return
  }

  // Check if any fields were filled in
  if (Object.keys(updateData).length === 0) {
    bulkEditError.value = 'Please fill in at least one field to update'
    return
  }

  bulkEditSubmitting.value = true
  bulkEditError.value = ''

  try {
    // Call bulk update API
    await api.patch('/equipment/bulk', {
      equipment_ids: bulkEditEquipmentIds.value,
      update_data: updateData
    })

    await loadEquipment()
    selectedItems.value = []
    closeBulkEditModal()
  } catch (error: any) {
    console.error('Error bulk updating equipment:', error)
    if (error.response?.status === 403) {
      bulkEditError.value = 'You do not have permission to perform bulk updates.'
    } else {
      bulkEditError.value = error.response?.data?.detail || 'Failed to update equipment. Please try again.'
    }
  } finally {
    bulkEditSubmitting.value = false
  }
}

// Add more of model functions
const openAddMoreModal = (item: any) => {
  equipmentToClone.value = item
  addMoreQuantity.value = 1
  addMoreStatus.value = 'processing'
  addMoreLocation.value = item.location || ''
  // Value carries over with the model, the donor does not unless it is specified here
  addMoreValue.value = item.value ?? null
  addMoreDonor.value = ''
  addMoreDonatedOn.value = ''
  addMoreCopyDonor.value = false
  addMoreError.value = ''
  showAddMoreModal.value = true
}

const closeAddMoreModal = () => {
  showAddMoreModal.value = false
  equipmentToClone.value = null
  addMoreQuantity.value = 1
  addMoreStatus.value = 'processing'
  addMoreLocation.value = ''
  addMoreValue.value = null
  addMoreDonor.value = ''
  addMoreDonatedOn.value = ''
  addMoreCopyDonor.value = false
  addMoreError.value = ''
}

// Ticking "same donor as original" fills the donor fields from the source item
watch(addMoreCopyDonor, (copy) => {
  if (copy && equipmentToClone.value) {
    addMoreDonor.value = equipmentToClone.value.donor || ''
    addMoreDonatedOn.value = toDateInput(equipmentToClone.value.donated_on)
  } else if (!copy) {
    addMoreDonor.value = ''
    addMoreDonatedOn.value = ''
  }
})

const confirmAddMore = async () => {
  if (!equipmentToClone.value || !addMoreQuantity.value || addMoreQuantity.value < 1) {
    addMoreError.value = 'Please enter a valid quantity'
    return
  }

  if (addMoreQuantity.value > 50) {
    addMoreError.value = 'Maximum quantity is 50 items at a time'
    return
  }

  addMoreSubmitting.value = true
  addMoreError.value = ''

  try {
    // Create equipment data by copying specs
    // Exclude status-based fields: claimed_by, claimed_for, claimed_on, distributed_on, distributed_by
    // Also exclude unique identifiers like id, asset_tag
    const equipmentData = {
      equipment_type: equipmentToClone.value.equipment_type,
      manufacturer: equipmentToClone.value.manufacturer,
      model: equipmentToClone.value.model,
      rating: equipmentToClone.value.rating,
      screen: equipmentToClone.value.screen,
      cpu: equipmentToClone.value.cpu,
      ram: equipmentToClone.value.ram,
      storage: equipmentToClone.value.storage,
      gpu: equipmentToClone.value.gpu,
      os: equipmentToClone.value.os,
      charger: equipmentToClone.value.charger,
      status: addMoreStatus.value, // Use the selected status from the form
      location: addMoreLocation.value, // Use the location from the form
      comments: equipmentToClone.value.comments,
      value: normalizeValue(addMoreValue.value), // Value copies from the original by default
      donor: addMoreDonor.value.trim(), // Donor only carries over when specified in the form
      donated_on: addMoreDonatedOn.value || undefined
      // Explicitly NOT copying: claimed_by, claimed_for, claimed_on, distributed_on, distributed_by, asset_tag, id
    }

    if (!equipmentData.donated_on) {
      delete equipmentData.donated_on
    }

    // Add multiple items
    for (let i = 0; i < addMoreQuantity.value; i++) {
      await api.post('/equipment', equipmentData)
    }

    await loadEquipment()
    closeAddMoreModal()
  } catch (error: any) {
    console.error('Error adding equipment:', error)
    if (error.response?.status === 403) {
      addMoreError.value = 'You do not have permission to add equipment.'
    } else {
      addMoreError.value = 'Failed to add equipment. Please try again.'
    }
  } finally {
    addMoreSubmitting.value = false
  }
}

// Donation receipt printing - a single item or a batch from the list
const printDonationReceipts = (equipmentIds: string[]) => {
  receiptEquipmentIds.value = equipmentIds
  receiptGroupByDonor.value = true
  showReceiptModal.value = true
}

const closeReceiptModal = () => {
  showReceiptModal.value = false
  receiptEquipmentIds.value = []
  receiptGroupByDonor.value = true
}

const receiptItemsMissingDonor = computed(() => {
  return equipment.value.filter(
    item => receiptEquipmentIds.value.includes(item.id) && !item.donor
  )
})

const confirmPrintReceipts = async () => {
  if (!receiptEquipmentIds.value.length) {
    return
  }

  receiptLoading.value = true

  try {
    const response = await api.post('/equipment/donation-receipts', {
      equipment_ids: receiptEquipmentIds.value,
      group_by_donor: receiptGroupByDonor.value
    })

    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(response.data)
      printWindow.document.close()
      setTimeout(() => printWindow.focus(), 250)
    } else {
      alert('Please allow popups for this site to print donation receipts.')
    }

    closeReceiptModal()
  } catch (error: any) {
    console.error('Error generating donation receipts:', error)
    alert('Failed to generate donation receipts. Please try again.')
  } finally {
    receiptLoading.value = false
  }
}

onMounted(() => {
  loadEquipment()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.inventory-view {
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-header h1 {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.5rem 0;
}

.page-header p {
  color: var(--text-light, #666666);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-add {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
}

.btn-add:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 163, 209, 0.4);
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 1.5rem;
  }

  .header-actions {
    width: 100%;
  }

  .btn-add {
    width: 100%;
  }
}
</style>
